/**
 * F-141: what the board's grid says about each short — the rows that belong
 * to it, the moods written across them, and its music coverage. The rail on
 * a Shorts board draws from this; the relational slots (`BoardSummary.shorts`)
 * say WHEN a short goes out and where it stands, and this says what is ON
 * the board for it. Pure over `BoardDoc`, kept beside `bgm.ts` because it
 * reads the same columns by the same rule.
 *
 * F-143: a Shorts board made since holds ONE Short and has no `Short`
 * column — its whole sheet is the short (`shortRunsForSlots`). The `Short`
 * column readers stay for the five-short weeks made before.
 */
import { SHORT_COLUMN_NAME } from "../shorts.js";
import { bgmColumns, boardBgmCoverage, findColumnByName } from "./bgm.js";
/**
 * One entry per short that has at least one row, in ascending slot order.
 * A board with no `Short` column has no runs; a `Short` value that is not a
 * whole number is ignored rather than invented.
 */
export function shortRuns(board, sheetId) {
    const shortColId = findColumnByName(board, sheetId, SHORT_COLUMN_NAME);
    if (shortColId === undefined)
        return [];
    const { runColId, runKind } = bgmColumns(board, sheetId);
    const moodColId = runKind === "mood" || runKind === "section" ? runColId : undefined;
    const bySlot = new Map();
    for (const rowId of board.sortedRowIds(sheetId)) {
        const raw = board.getCellText(sheetId, rowId, shortColId).trim();
        if (!/^\d+$/.test(raw))
            continue;
        const slot = Number(raw);
        let run = bySlot.get(slot);
        if (run === undefined) {
            run = { slot, rowIds: [], topRowId: rowId, moods: [], music: { covered: 0, total: 0 } };
            bySlot.set(slot, run);
        }
        run.rowIds.push(rowId);
        if (moodColId !== undefined) {
            const mood = board.getCellText(sheetId, rowId, moodColId).trim();
            if (mood !== "" && !run.moods.includes(mood))
                run.moods.push(mood);
        }
    }
    for (const musicRun of boardBgmCoverage(board, sheetId).runs) {
        const slot = Number(musicRun.group);
        const run = bySlot.get(slot);
        if (run === undefined)
            continue;
        run.music.total += 1;
        if (musicRun.covered)
            run.music.covered += 1;
    }
    return [...bySlot.values()].sort((a, b) => a.slot - b.slot);
}
/**
 * F-143: the one run of a one-Short board — the whole sheet. Every row, the
 * moods written down its Mood (or Section) column, and the music runs of
 * the whole sheet. Undefined on a sheet with no rows.
 */
export function wholeSheetShortRun(board, sheetId, slot) {
    const rowIds = board.sortedRowIds(sheetId);
    const topRowId = rowIds[0];
    if (topRowId === undefined)
        return undefined;
    const { runColId, runKind } = bgmColumns(board, sheetId);
    const moodColId = runKind === "mood" || runKind === "section" ? runColId : undefined;
    const moods = [];
    if (moodColId !== undefined) {
        for (const rowId of rowIds) {
            const mood = board.getCellText(sheetId, rowId, moodColId).trim();
            if (mood !== "" && !moods.includes(mood))
                moods.push(mood);
        }
    }
    const music = { covered: 0, total: 0 };
    for (const musicRun of boardBgmCoverage(board, sheetId).runs) {
        music.total += 1;
        if (musicRun.covered)
            music.covered += 1;
    }
    return { slot, rowIds, topRowId, moods, music };
}
/**
 * The runs for a board's slots (`BoardSummary.shorts`' slot numbers): the
 * `Short` column's runs when the sheet has them, else — a one-Short board
 * (F-143) — the whole sheet as its one slot's run. A board with several
 * slots and no `Short` column has no runs, as before.
 */
export function shortRunsForSlots(board, sheetId, slots) {
    const runs = shortRuns(board, sheetId);
    const only = slots.length === 1 ? slots[0] : undefined;
    if (runs.length > 0 || only === undefined)
        return runs;
    const whole = wholeSheetShortRun(board, sheetId, only);
    return whole === undefined ? [] : [whole];
}
/**
 * Each short's Original Script lines, in slot order — what the per-short
 * sampler (`sampleShortsScript`) is fed. Empty when the sheet has no
 * `Short` column or no source column.
 */
export function readShortsScriptLines(board, sheetId) {
    const colId = board.sourceColumnId(sheetId);
    if (colId === undefined)
        return [];
    return shortRuns(board, sheetId).map((run) => ({
        slot: run.slot,
        lines: run.rowIds.map((rowId) => board.getCellText(sheetId, rowId, colId)),
    }));
}
//# sourceMappingURL=short-runs.js.map